// ───────────────────────────────────────────────────────────────────────────
// Color palettes — curated (primary, accent) duos for dossier customization.
//
// Each dossier may carry a `color_override` of shape { primary, accent }.
// When set, applyColorOverride() rewrites the matching theme tokens so the
// React preview and the Jinja2 PDF render with the user's chosen colors
// instead of the template's defaults.
// ───────────────────────────────────────────────────────────────────────────

// Swiss Grid defaults (mirror backend/templates/swiss_grid.html :root)
export const DEFAULT_ACCENT = '#2563EB';
export const DEFAULT_INK = '#0a0a0a';

// One-click highlight (accent) colors — recolor section numbers, chips,
// language badges, date pills, seniority, availability dot, "CONFIDENTIEL".
export const ACCENT_PRESETS = [
  { id: 'azur',     label: 'Azur',     color: '#2563EB' },
  { id: 'indigo',   label: 'Indigo',   color: '#4F46E5' },
  { id: 'violet',   label: 'Violet',   color: '#7C3AED' },
  { id: 'emeraude', label: 'Émeraude', color: '#059669' },
  { id: 'sarcelle', label: 'Sarcelle', color: '#0D9488' },
  { id: 'ambre',    label: 'Ambre',    color: '#D97706' },
  { id: 'corail',   label: 'Corail',   color: '#EA580C' },
  { id: 'rose',     label: 'Rose',     color: '#DB2777' },
  { id: 'rouge',    label: 'Rouge',    color: '#DC2626' },
  { id: 'ardoise',  label: 'Ardoise',  color: '#475569' },
];

// Title / trigram (ink) colors — default near-black.
export const TITLE_PRESETS = [
  { id: 'noir',   label: 'Noir',   color: '#0a0a0a' },
  { id: 'encre',  label: 'Encre',  color: '#0f172a' },
  { id: 'marine', label: 'Marine', color: '#0b1e4d' },
  { id: 'prune',  label: 'Prune',  color: '#3b0764' },
];

// Token groups → which theme keys receive each override color.
// Kept explicit (not regex) so we never accidentally rewrite a structural
// color (e.g. a footer border that must stay light grey).
const PRIMARY_TOKENS = [
  'ink',
  'trigramColor',
  'posteColor',
  'headingColor',
  'cpHeaderName',
  'sHeadText',
  'line',
];

const ACCENT_TOKENS = [
  'accent',
  'accentBar',
  'switchAccent',
  'activeBorder',
  'sNumBg',
  'chipText',
  'badgeText',
  'cpFooterBrand',
];

// Normalize input to a #rrggbb hex or null if unrecognised.
export const normalizeHex = (val) => {
  if (typeof val !== 'string') return null;
  const v = val.trim();
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(v);
  if (!m) return null;
  let hex = m[1];
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  return '#' + hex.toLowerCase();
};

// Return a theme object with override colors substituted in. Idempotent
// when override is null/empty.
export const applyColorOverride = (theme, override) => {
  if (!theme || !override) return theme;
  const primary = normalizeHex(override.primary);
  const accent = normalizeHex(override.accent);
  if (!primary && !accent) return theme;

  const next = { ...theme };

  if (primary) {
    for (const k of PRIMARY_TOKENS) next[k] = primary;
  }

  if (accent) {
    for (const k of ACCENT_TOKENS) next[k] = accent;
    // Soft tints derived from the accent for chip / badge / date backgrounds.
    next.chipBg = accent + '1a';
    next.badgeBg = accent + '1a';
    next.dateBg = accent + '14';
  }

  return next;
};
